import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FieldValueInterface } from '@vendasta/crm';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { BlankValueComponent, CustomCellComponent } from '@vendasta/galaxy/table';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { StandardIds } from '../../../shared-services';
import { ActivityTaskStatus } from '../../../tokens-and-interfaces';

@Component({
  template: `
    <ng-container *ngIf="date; else blank">
      <div class="overdue-task" *ngIf="isOverdue; else notOverdue">
        <span>{{ date | glxyDate: dateFormat }}</span>
        <span *ngIf="daysOverdue > 0; else showInHours">
          ({{ 'TASK.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.DAYS_OVERDUE' | translate: { amount: daysOverdue } }})
        </span>
        <ng-template #showInHours
          >({{
            'TASK.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.HOURS_OVERDUE' | translate: { amount: hoursOverdue }
          }})</ng-template
        >
      </div>
      <ng-template #notOverdue>
        {{ date | glxyDate: dateFormat }}
      </ng-template>
    </ng-container>

    <ng-template #blank>
      <glxy-blank-value></glxy-blank-value>
    </ng-template>
  `,
  standalone: true,
  imports: [CommonModule, BlankValueComponent, GalaxyPipesModule, TranslateModule],
  styleUrls: ['./due-date.component.scss'],
})
export class DueDateCellComponent extends CustomCellComponent {
  protected date: Date | undefined;
  protected isOverdue = false;
  protected daysOverdue = 0;
  protected hoursOverdue = 0;
  protected dateFormat = DateFormat.medium;
  private readonly milisecToHours = 3600000;

  set value(newValues: FieldValueInterface[]) {
    const dueDateField = newValues.filter(Boolean).find((field) => field?.fieldId === StandardIds.ActivityTaskDueDate);
    this.date = dueDateField?.dateValue || undefined;

    const statusField = newValues.filter(Boolean).find((field) => field?.fieldId === StandardIds.ActivityTaskStatus);
    const timeOverdue = this.getTimeOverdue(this.date);
    this.isOverdue = timeOverdue.isOverdue && statusField?.stringValue === ActivityTaskStatus.Open;
    this.daysOverdue = timeOverdue.days;
    this.hoursOverdue = timeOverdue.hours;
  }

  /*
  Check if a dueDate has passed and calculate the days and hours it is overdue by
  Something is overdue if the current time is at least an hour past the due date
   */
  private getTimeOverdue(dueDate?: Date): { days: number; hours: number; isOverdue: boolean } {
    if (!dueDate) {
      return { days: 0, hours: 0, isOverdue: false };
    }
    const todayHours = Date.now() / this.milisecToHours || 0;
    const dueDateHours = dueDate?.getTime() / this.milisecToHours || 0;
    if (todayHours === 0 || dueDateHours === 0) {
      return { days: 0, hours: 0, isOverdue: false };
    }
    const hoursOverdue = todayHours - dueDateHours;
    return {
      days: Math.floor(hoursOverdue / 24),
      hours: Math.floor(hoursOverdue),
      isOverdue: Math.floor(hoursOverdue) > 0,
    };
  }

  constructor() {
    super();
  }
}
